import { useState } from "react"
import {
  X,
  Mail,
  Phone,
  MapPin,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  Award,
  BookOpen,
  Clock,
  MessageCircle,
  Star,
  Video,
  DollarSign,
  Download,
  Eye,
} from "lucide-react"
import { useLearnerDetails } from "../../../hooks/useStats"
import LearnerCourseProgressModal from "./LearnerCourseProgressModal"

const formatNumber = (v) => (v ?? 0).toLocaleString("fr-FR")
const formatMoney = (v, currency = "XOF") =>
  `${(v ?? 0).toLocaleString("fr-FR")} ${currency === "XOF" ? "FCFA" : currency}`
const formatDate = (d) => {
  if (!d) return "—"
  try {
    return new Date(d).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  } catch {
    return "—"
  }
}
const formatDateTime = (d) => {
  if (!d) return "—"
  try {
    return new Date(d).toLocaleString("fr-FR")
  } catch {
    return "—"
  }
}
const formatDuration = (seconds) => {
  if (!seconds) return "0h"
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}min`
  return `${m}min`
}

const STATUS_BADGES = {
  ACTIVE: { label: "Actif", class: "bg-green-100 text-green-800" },
  INACTIVE: { label: "Inactif", class: "bg-gray-100 text-gray-800" },
  SUSPENDED: { label: "Suspendu", class: "bg-yellow-100 text-yellow-800" },
  BANNED: { label: "Banni", class: "bg-red-100 text-red-800" },
  IN_PROGRESS: { label: "En cours", class: "bg-blue-100 text-blue-800" },
  COMPLETED: { label: "Terminé", class: "bg-green-100 text-green-800" },
  DROPPED: { label: "Abandonné", class: "bg-red-100 text-red-800" },
  SUCCESS: { label: "Réussi", class: "bg-green-100 text-green-800" },
  PENDING: { label: "En attente", class: "bg-yellow-100 text-yellow-800" },
  FAILED: { label: "Échec", class: "bg-red-100 text-red-800" },
}

const StatusBadge = ({ status }) => {
  const s = STATUS_BADGES[status] || { label: status, class: "bg-gray-100 text-gray-800" }
  return (
    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${s.class}`}>
      {s.label}
    </span>
  )
}

const ProgressBar = ({ value, max = 100, color = "#203a8a" }) => {
  const pct = Math.min(100, Math.max(0, (value / max) * 100))
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
      <div
        className="h-2 rounded-full transition-all"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  )
}

const SummaryCard = ({ icon: Icon, label, value, color = "text-blue-600", bg = "bg-blue-50" }) => (
  <div className="bg-white rounded-lg p-4 border border-gray-200">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${bg}`}>
        <Icon size={18} className={color} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-bold text-gray-900 truncate">{value}</p>
      </div>
    </div>
  </div>
)

export default function LearnerDetailModal({ isOpen, userId, onClose }) {
  const [activeTab, setActiveTab] = useState("profile")
  const [selectedCourseId, setSelectedCourseId] = useState(null)
  const { data, loading, error } = useLearnerDetails(isOpen ? userId : null)

  if (!isOpen) return null

  const user = data?.user
  const summary = data?.summary
  const courses = data?.courses || []
  const certificates = data?.certificates || []
  const payments = data?.payments || []
  const webinars = data?.webinars || []
  const currency = summary?.currency || "XOF"

  const tabs = [
    { id: "profile", label: "Profil", icon: User },
    { id: "courses", label: `Formations (${courses.length})`, icon: BookOpen },
    { id: "certificates", label: `Certificats (${certificates.length})`, icon: Award },
    { id: "payments", label: `Paiements (${payments.length})`, icon: DollarSign },
    { id: "webinars", label: `Webinaires (${webinars.length})`, icon: Video },
  ]

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 p-6 border-b border-gray-200">
            <div className="flex items-center gap-4 min-w-0">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-[#203a8a]"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#203a8a] text-white flex items-center justify-center text-2xl font-bold flex-shrink-0">
                  {user?.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
              )}
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-[#203a8a] truncate">
                  {loading ? "Chargement…" : user?.name || "Apprenant"}
                </h2>
                <p className="text-sm text-gray-600 truncate">{user?.email}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {user?.role && (
                    <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">
                      {user.role}
                    </span>
                  )}
                  {user?.status && <StatusBadge status={user.status} />}
                  {user?.isVerified && (
                    <span className="inline-flex items-center gap-1 text-xs text-green-700">
                      <CheckCircle size={12} /> Vérifié
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"
            >
              <X size={20} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 px-6 border-b border-gray-200 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? "border-[#203a8a] text-[#203a8a]"
                      : "border-transparent text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              )
            })}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#203a8a]"></div>
              </div>
            )}

            {error && !loading && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                <p className="font-semibold">Erreur</p>
                <p className="text-sm">{error}</p>
              </div>
            )}

            {!loading && !error && data && (
              <>
                {/* PROFILE TAB */}
                {activeTab === "profile" && (
                  <div className="space-y-6">
                    {/* Summary cards */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">
                        Résumé d'activité
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        <SummaryCard
                          icon={BookOpen}
                          label="Inscriptions"
                          value={formatNumber(summary?.totalEnrollments)}
                          color="text-blue-600"
                          bg="bg-blue-50"
                        />
                        <SummaryCard
                          icon={CheckCircle}
                          label="Cours complétés"
                          value={formatNumber(summary?.completedEnrollments)}
                          color="text-green-600"
                          bg="bg-green-50"
                        />
                        <SummaryCard
                          icon={Award}
                          label="Certificats"
                          value={formatNumber(summary?.totalCertificates)}
                          color="text-yellow-600"
                          bg="bg-yellow-50"
                        />
                        <SummaryCard
                          icon={Star}
                          label="Taux complétion"
                          value={`${summary?.completionRate ?? 0}%`}
                          color="text-purple-600"
                          bg="bg-purple-50"
                        />
                        <SummaryCard
                          icon={Clock}
                          label="Temps passé"
                          value={formatDuration(summary?.totalTimeSpentSeconds)}
                          color="text-indigo-600"
                          bg="bg-indigo-50"
                        />
                        <SummaryCard
                          icon={Award}
                          label="Quiz réussis"
                          value={`${summary?.passedQuizAttempts ?? 0}/${summary?.totalQuizAttempts ?? 0}`}
                          color="text-emerald-600"
                          bg="bg-emerald-50"
                        />
                        <SummaryCard
                          icon={MessageCircle}
                          label="Commentaires"
                          value={formatNumber(summary?.totalComments)}
                          color="text-cyan-600"
                          bg="bg-cyan-50"
                        />
                        <SummaryCard
                          icon={DollarSign}
                          label="Revenus générés"
                          value={formatMoney(summary?.totalRevenue, currency)}
                          color="text-pink-600"
                          bg="bg-pink-50"
                        />
                      </div>
                    </div>

                    {/* User info */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">
                        Informations personnelles
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InfoRow icon={Mail} label="Email" value={user?.email} />
                        <InfoRow icon={Phone} label="Téléphone" value={user?.phone} />
                        <InfoRow
                          icon={Calendar}
                          label="Date de naissance"
                          value={formatDate(user?.birthDate)}
                        />
                        <InfoRow icon={User} label="Genre" value={user?.gender} />
                        <InfoRow
                          icon={MapPin}
                          label="Localisation"
                          value={
                            [user?.city, user?.country].filter(Boolean).join(", ") || "—"
                          }
                        />
                        <InfoRow icon={MapPin} label="Adresse" value={user?.address} />
                        <InfoRow
                          icon={Calendar}
                          label="Inscrit le"
                          value={formatDate(user?.createdAt)}
                        />
                        <InfoRow
                          icon={Calendar}
                          label="Mis à jour le"
                          value={formatDate(user?.updatedAt)}
                        />
                        <InfoRow
                          icon={User}
                          label="Langue préférée"
                          value={user?.preferredLanguage}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* COURSES TAB */}
                {activeTab === "courses" && (
                  <div className="space-y-4">
                    {courses.length === 0 ? (
                      <EmptyState message="Aucune formation" />
                    ) : (
                      courses.map((c) => (
                        <div
                          key={c.enrollment.id}
                          className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start gap-4">
                            {c.course.thumbnail && (
                              <img
                                src={c.course.thumbnail}
                                alt={c.course.title}
                                className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0">
                                  <h4 className="font-semibold text-[#203a8a] truncate">
                                    {c.course.title}
                                  </h4>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <span className="text-xs text-gray-500">
                                      {c.course.category?.name || "—"}
                                    </span>
                                    <span className="text-xs text-gray-400">•</span>
                                    <span className="text-xs text-gray-500">
                                      {c.course.level}
                                    </span>
                                    <span className="text-xs text-gray-400">•</span>
                                    <span className="text-xs text-gray-500">
                                      {formatDuration(c.course.totalDuration)}
                                    </span>
                                    <StatusBadge status={c.enrollment.status} />
                                  </div>
                                </div>
                                <button
                                  onClick={() => setSelectedCourseId(c.course.id)}
                                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-[#203a8a] hover:bg-[#2a4cb5] rounded-lg flex-shrink-0"
                                >
                                  <Eye size={14} />
                                  Détails
                                </button>
                              </div>

                              <div className="mb-2">
                                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                  <span>
                                    Progression : {c.progress.completedLessons}/
                                    {c.progress.totalLessons} leçons
                                  </span>
                                  <span className="font-semibold">
                                    {c.progress.progress}%
                                  </span>
                                </div>
                                <ProgressBar value={c.progress.progress} />
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
                                <Stat label="Terminées" value={c.lessons.completed} />
                                <Stat label="En cours" value={c.lessons.inProgress} />
                                <Stat
                                  label="Non démarrées"
                                  value={c.lessons.notStarted}
                                />
                                <Stat
                                  label="Temps passé"
                                  value={formatDuration(c.blocks.totalTimeSpent)}
                                />
                              </div>

                              {c.quizzes && c.quizzes.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                  <p className="text-xs font-semibold text-gray-700 mb-2">
                                    Quiz ({c.quizzes.length})
                                  </p>
                                  <div className="space-y-1">
                                    {c.quizzes.map((q) => (
                                      <div
                                        key={q.quizId}
                                        className="flex items-center justify-between text-xs"
                                      >
                                        <span className="truncate">{q.title}</span>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                          <span
                                            className={
                                              q.passed
                                                ? "text-green-700"
                                                : "text-red-700"
                                            }
                                          >
                                            {q.bestScore}/{q.maxScore}
                                          </span>
                                          {q.passed ? (
                                            <CheckCircle
                                              size={12}
                                              className="text-green-600"
                                            />
                                          ) : (
                                            <XCircle
                                              size={12}
                                              className="text-red-600"
                                            />
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {c.certificate && (
                                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-yellow-700">
                                  <Award size={14} />
                                  <span>Certificat : {c.certificate.certificateNumber}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* CERTIFICATES TAB */}
                {activeTab === "certificates" && (
                  <div className="space-y-3">
                    {certificates.length === 0 ? (
                      <EmptyState message="Aucun certificat" />
                    ) : (
                      certificates.map((cert) => (
                        <div
                          key={cert.id}
                          className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="p-3 bg-yellow-50 rounded-lg">
                              <Award size={20} className="text-yellow-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 truncate">
                                {cert.certificateNumber}
                              </p>
                              <p className="text-xs text-gray-500">
                                Émis le {formatDate(cert.issuedAt)}
                                {cert.expiresAt &&
                                  ` · Expire le ${formatDate(cert.expiresAt)}`}
                              </p>
                            </div>
                          </div>
                          {cert.pdfUrl && (
                            <a
                              href={cert.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#203a8a] border border-[#203a8a] hover:bg-[#203a8a] hover:text-white rounded-lg flex-shrink-0"
                            >
                              <Download size={14} />
                              PDF
                            </a>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* PAYMENTS TAB */}
                {activeTab === "payments" && (
                  <div className="overflow-x-auto">
                    {payments.length === 0 ? (
                      <EmptyState message="Aucun paiement" />
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
                          <tr>
                            <th className="px-4 py-3">Date</th>
                            <th className="px-4 py-3">Référence</th>
                            <th className="px-4 py-3">Type</th>
                            <th className="px-4 py-3">Méthode</th>
                            <th className="px-4 py-3 text-right">Montant</th>
                            <th className="px-4 py-3">Statut</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {payments.map((p) => (
                            <tr key={p.id}>
                              <td className="px-4 py-3 text-gray-600">
                                {formatDateTime(p.createdAt)}
                              </td>
                              <td className="px-4 py-3 font-mono text-xs">
                                {p.transactionRef || p.id}
                              </td>
                              <td className="px-4 py-3">{p.type}</td>
                              <td className="px-4 py-3">{p.method}</td>
                              <td className="px-4 py-3 text-right font-semibold">
                                {formatMoney(p.amount, currency)}
                              </td>
                              <td className="px-4 py-3">
                                <StatusBadge status={p.status} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* WEBINARS TAB */}
                {activeTab === "webinars" && (
                  <div className="space-y-3">
                    {webinars.length === 0 ? (
                      <EmptyState message="Aucun webinaire" />
                    ) : (
                      webinars.map((w) => (
                        <div
                          key={w.id}
                          className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="p-3 bg-pink-50 rounded-lg">
                              <Video size={20} className="text-pink-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 truncate">
                                {w.webinar?.title || "Webinaire"}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatDateTime(w.webinar?.date)} ·{" "}
                                {w.webinar?.status}
                              </p>
                              <p className="text-xs text-gray-400">
                                Inscrit le {formatDate(w.registeredAt)}
                                {w.cancelledAt &&
                                  ` · Annulé le ${formatDate(w.cancelledAt)}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            {w.attended ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
                                <CheckCircle size={12} /> Présent
                              </span>
                            ) : w.cancelledAt ? (
                              <span className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-50 px-2 py-1 rounded-full">
                                <XCircle size={12} /> Annulé
                              </span>
                            ) : (
                              <span className="text-xs text-gray-500">Inscrit</span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Sub-modal : progression détaillée d'un cours */}
      <LearnerCourseProgressModal
        isOpen={!!selectedCourseId}
        userId={userId}
        courseId={selectedCourseId}
        onClose={() => setSelectedCourseId(null)}
      />
    </>
  )
}

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
    <div className="min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm text-gray-900 truncate">{value || "—"}</p>
    </div>
  </div>
)

const Stat = ({ label, value }) => (
  <div className="bg-gray-50 rounded p-2 text-center">
    <p className="text-gray-500">{label}</p>
    <p className="font-semibold text-gray-900">{value}</p>
  </div>
)

const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-16 text-gray-500">
    <p className="text-sm">{message}</p>
  </div>
)
